import { getAuth } from "firebase/auth";
import { addDoc, collection, doc, getDoc, getDocs, updateDoc } from "firebase/firestore"; // Import Firestore methods
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import "../css/SuppliersAddReceipt.css";
import { db } from "../firebase-config"; // Import the db object for Firebase
import Swal from "sweetalert2";

const SuppliersAddReceipt = () => {
  const [dateTime, setDateTime] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [supplierCode, setSupplierCode] = useState('');
  const [date, setDate] = useState('');
  const [items, setItems] = useState([{ item: '', quantity: '', measurement: '', price: '', totalItemPrice: '' }]);
  const [productsItems, setProductsItems] = useState([{ item: '', quantity: '', measurement: '', price: '', totalProductPrice: '' }]);
  const [ingredients, setIngredients] = useState([]); // State to store ingredients list
  const [products, setProducts] = useState([]); // State to store products list
  const [lastUpdatedBy, setLastUpdatedBy] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const auth = getAuth(); // Get the Firebase Auth instance
  const currentUser = auth.currentUser; // Get the current logged-in user
  
  useEffect(() => {
    if (currentUser) {
      setLastUpdatedBy(currentUser.email); // Automatically set the logged-in user's email
    }
  }, [currentUser]);

  
  useEffect(() => {
    const today = new Date();
    
    // Get the local timezone offset in minutes
    const offset = today.getTimezoneOffset();
    
    // Adjust the time for local timezone
    const localDate = new Date(today.getTime() - offset * 60000); // Correcting for the time zone offset
    
    // Format the date and time to 'YYYY-MM-DDTHH:mm'
    const formattedDateTime = localDate.toISOString().slice(0, 16);
    setDateTime(formattedDateTime);
  }, []); // Only run once when the component mounts

  // Fetch ingredients list from Firestore when the component mounts
  useEffect(() => {
    const fetchIngredients = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "ingredients"));
        const ingredientsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        const sortedIngredients = ingredientsData.sort((a, b) => {
          // Normalize both names to lowercase for case-insensitive sorting
          const nameA = a.name.toLowerCase();
          const nameB = b.name.toLowerCase();
  
          if (nameA < nameB) return -1;
          if (nameA > nameB) return 1;
          return 0;
        });
  
        setIngredients(sortedIngredients);
      } catch (error) {
        console.error("Error fetching ingredients: ", error);
      }
    };
  
    fetchIngredients();
  
    // Set the current date and time
    const currentDate = new Date();
    const formattedDate = currentDate.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true, // Use 12-hour format with AM/PM
    });
    setDate(formattedDate); // Update the state with the full date and time
  }, []); // This runs only once, when the component mounts

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "products"));
        const productsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
  
        const sortedProducts = productsData.sort((a, b) => {
          const nameA = a.name.toLowerCase();
          const nameB = b.name.toLowerCase();
  
          if (nameA < nameB) return -1;
          if (nameA > nameB) return 1;
          return 0;
        });
  
        setProducts(sortedProducts);
      } catch (error) {
        console.error("Error fetching products: ", error);
      }
    };
  
    fetchProducts();
  }, []); // Run only once when the component mounts

  
    // Function to add a log entry
    const addLogEntry = async (itemData) => {
      try {
        const currentDate = new Date();

        const formattedDate = [
          currentDate.getMonth() + 1,
          currentDate.getDate(),
          currentDate.getFullYear()
        ].join('/') + ' ' +
        [currentDate.getHours(), currentDate.getMinutes()].join(':');

        await addDoc(collection(db, "item_logs"), {
          itemName: itemData.name,
          typeOfChange: 'Supplier',
          lastUpdatedBy: lastUpdatedBy.split('@')[0],
          date: formattedDate,
          descriptionOfChange: 'Added',
          quantity: parseFloat(itemData.quantity),
          editChangeLog: '-',
        });
      } catch (error) {
        console.error('Error adding log:', error);
      }
    };

    const addPriceLogEntry = async (itemData, supplierName) => {
      try {
        const currentDate = new Date();

        const formattedDate = [
          currentDate.getMonth() + 1,
          currentDate.getDate(),
          currentDate.getFullYear()
        ].join('/') + ' ' +
        [currentDate.getHours(), currentDate.getMinutes()].join(':');

        const originalPrice = parseFloat(itemData.originalPrice || 0);
        const newPrice = parseFloat(itemData.price || 0);

        if(originalPrice !== newPrice) {
          await addDoc(collection(db, "price_logs"), {
            itemName: itemData.name,
            typeOfChange: 'Suppliers',
            lastUpdatedBy: lastUpdatedBy.split('@')[0],
            date: formattedDate,
            supplierName: supplierName,
            priceChangeLog:  `${originalPrice} -> ${newPrice}`,
          });
        }

        
      } catch (error) {
        console.error('Error adding log:', error);
      }
    };

  // Handle change in the fields
  const handleInputChange = (index, e) => {
    const values = [...items];
    values[index][e.target.name] = e.target.value;
  
    // If the selected field is "item", fetch the price based on the name
    if (e.target.name === 'item') {
      const selectedIngredient = ingredients.find(ingredient => ingredient.name === e.target.value);
      if (selectedIngredient) {
        values[index].originalPrice = selectedIngredient.dealerPrice; // Store the original price
        values[index].price = selectedIngredient.dealerPrice; // Set the price automatically
        values[index].measurement = selectedIngredient.measurement;
      }
    }
  
    setItems(values);
  };

  const handleProductChange = (index, e) => {
    const values = [...productsItems];
    values[index][e.target.name] = e.target.value;
    
    if (e.target.name === 'item') {
      const selectedProduct = products.find(product => product.name === e.target.value);
      if (selectedProduct) {
        values[index].productOriginalPrice = selectedProduct.dealerPrice; // Store the original price
        values[index].price = selectedProduct.dealerPrice || ''; // Set the price automatically or fallback to empty
        values[index].measurement = selectedProduct.measurement;
      }
    }
  
    setProductsItems(values);
  };

  // Add a new item row
  const addItemRow = () => {
    setItems([...items, { item: '', quantity: '', measurement: '', price: '', totalItemPrice: '' }]);
  };

  // Remove an item row
  const removeItemRow = (index) => {
    const values = [...items];
    values.splice(index, 1);
    setItems(values);
  };

  const addProductRow = () => {
    setProductsItems([...productsItems, { item: '', quantity: '', measurement: '', price: '', totalProductPrice: '' }]);
  };

  const removeProductRow = (index) => {
    const values = [...productsItems];
    values.splice(index, 1);
    setProductsItems(values);
  };

  const handleDateTimeChange = (e) => {
    setDateTime(e.target.value);
  };

  const shippingFee = (parseFloat(items[0]?.shippingFee) || 0).toFixed(2);
  const shippingFeeValue = parseFloat(shippingFee);

  const subtotal = (
    [...items, ...productsItems].reduce(
      (total, item) =>
        total + (parseFloat(item.quantity) || 0) * (parseFloat(item.price) || 0),
      0
    )
  ).toFixed(2);

  // Calculate the total amount (quantity * price)
  const totalAmount = (
    [...items, ...productsItems].reduce(
      (total, item) =>
        total + (parseFloat(item.quantity) || 0) * (parseFloat(item.price) || 0),
      0
    ) + shippingFeeValue
  ).toFixed(2);

  const resetForm = () => {
    setSupplierName('');
    setSupplierCode('');
    setItems([{ item: '', quantity: '', measurement: '', price: '' }]);
    setProductsItems([{ item: '', quantity: '', measurement: '', price: '' }]);
  };



  // Handle form submission (save to Firestore)
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!supplierName || !date) {
      toast.error("Please fill in all required fields (Branch Name and Date).", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
        progress: undefined,
      });
      return;
  }

  // Check if all items have a valid selection, quantity, and price
  // const invalidItems = items.filter(item => !item.item || !item.quantity || !item.price);
  // if (invalidItems.length > 0) {
  //     toast.error("Please fill in all fields for each item.", {
  //       position: "top-right",
  //       autoClose: 5000,
  //       hideProgressBar: false,
  //       closeOnClick: true,
  //       pauseOnHover: false,
  //       draggable: true,
  //       progress: undefined,
  //     });
  //     return;
  // }

  // const invalidProducts = productsItems.filter(products => !products.item || !products.quantity || !products.price);
  // if (invalidProducts.length > 0) {
  //     toast.error("Please fill in all fields for each product.", {
  //       position: "top-right",
  //       autoClose: 5000,
  //       hideProgressBar: false,
  //       closeOnClick: true,
  //       pauseOnHover: false,
  //       draggable: true,
  //       progress: undefined,
  //     });
  //     return;
  // }

    const currentDate = new Date();

      const formattedDate = [
        currentDate.getMonth() + 1,
        currentDate.getDate(),
        currentDate.getFullYear()
      ].join('/') + ' ' +
      [currentDate.getHours(), currentDate.getMinutes()].join(':');
  
    // Form data to be saved
    const formData = { supplierName, supplierCode, date: formattedDate, items, products: productsItems, subtotal, shippingFee, totalAmount, lastUpdatedBy: auth.currentUser.email };
  
    const confirmSubmit = await Swal.fire({
      title: 'Confirm Submission',
      text: "You are about to submit this receipt. Make sure that the details are correct",
      icon: 'info',
      showCancelButton: true,
      confirmButtonText: 'Confirm',
      cancelButtonText: 'Cancel',
    })

    if (confirmSubmit.isConfirmed) {
      try {
        setIsLoading(true); // Set loading state to true
  
        // Add a new document to the "supplierReceipts" collection with the full date-time
        const docRef = await addDoc(collection(db, "supplierReceipts"), formData);
        console.log("Document written with ID: ", docRef.id);
    
        // Now update the ingredient quantities
        for (const item of items) {
          if (item.item && item.quantity) {
            // Find the ingredient by name
            const selectedIngredient = ingredients.find(ingredient => ingredient.name === item.item);
    
            if (selectedIngredient) {
              const ingredientRef = doc(db, 'ingredients', selectedIngredient.id); // Use the ingredient id
              const ingredientSnapshot = await getDoc(ingredientRef);
    
              if (ingredientSnapshot.exists()) {
                const ingredientData = ingredientSnapshot.data();
                const newQuantity = parseFloat(ingredientData.quantity) + parseFloat(item.quantity);
    
                // Update the ingredient's quantity in Firestore
                await updateDoc(ingredientRef, { quantity: newQuantity });
                console.log(`Updated ingredient ${item.item} with new quantity: ${newQuantity}`);
    
                // Add log entry
                await addLogEntry({
                  name: ingredientData.name,
                  quantity: item.quantity,
                  lastUpdatedBy: lastUpdatedBy
                });
  
                
                //Add Price Log entry
                await addPriceLogEntry({
                  name: ingredientData.name,
                  originalPrice: item.originalPrice,
                  price: item.price,
                  lastUpdatedBy: lastUpdatedBy
              }, supplierName)
              } else {
                console.log(`Ingredient with name ${item.item} not found!`);
              }
            } else {
              console.log(`Ingredient with name ${item.item} not found!`);
            }
          }
        }
  
        for (const product of productsItems) {
          if (product.item && product.quantity) {
            const selectedProduct = products.find(prod => prod.name === product.item);
        
            if (selectedProduct) {
              const productRef = doc(db, 'products', selectedProduct.id);
              const productSnapshot = await getDoc(productRef);
        
              if (productSnapshot.exists()) {
                const productData = productSnapshot.data();
                const newQuantity = parseFloat(productData.quantity) + parseFloat(product.quantity);
        
                await updateDoc(productRef, { quantity: newQuantity });
                console.log(`Updated product ${product.item} with new quantity: ${newQuantity}`);
        
                // Add log entry
                await addLogEntry({
                  name: productData.name,
                  quantity: product.quantity,
                  lastUpdatedBy: lastUpdatedBy
                });
  
                //Add Price Log entry
                await addPriceLogEntry({
                    name: productData.name,
                    originalPrice: product.productOriginalPrice,
                    price: product.price,
                    lastUpdatedBy: lastUpdatedBy
                }, supplierName)
              }
            }
          }
        }
  
        toast.success("Receipt Added and Ingredients Updated Successfully!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: true,
          progress: undefined,
        });
  
         // Call resetForm after all operations are completed
      resetForm();
      } catch (e) {
        console.error("Error adding document: ", e);
        alert("Error adding receipt. Please try again.");
      } finally {
        setIsLoading(false); // Reset loading state
      }
    }
    
  };

  return (
    <form onSubmit={handleSubmit}>
      <h1>Add Supplier Receipt</h1><br /><br />

      {/* First Row: Supplier Name, Code, and Date */}
      <div className="form-row">
        <div className="form-field">
          <label>Supplier Name:</label>
          <input
            type="text"
            value={supplierName}
            onChange={(e) => setSupplierName(e.target.value)}
            
          />
        </div>

        <div className="form-field">
          <label>Supplier Code:</label>
          <input
            type="text"
            value={supplierCode}
            onChange={(e) => setSupplierCode(e.target.value)}
            
          />
        </div>

        <div className="form-field">
          <label>Date:</label>
          <input
            type="datetime-local"
            value={dateTime}
            onChange={handleDateTimeChange}
            
          />
        </div>
      </div>

      {/* Loop over the items array to generate item rows dynamically */}
      {items.map((item, index) => (
        <div key={index} className="form-row">
          <div className="form-field">
            <label>Ingredients:</label>
            <select
              name="item"
              value={item.item}
              onChange={(e) => handleInputChange(index, e)}
              
            >
              <option value="">Select Item</option>
              {ingredients.map((ingredient, idx) => (
                <option key={idx} value={ingredient.name}>
                {ingredient.name}
              </option>
              ))}
            </select>
          </div>

          <div className="form-field">
            <label>Quantity:</label>
            <input
              type="number"
              name="quantity"
              value={item.quantity}
              onChange={(e) => handleInputChange(index, e)}
              
            />
          </div>

          <div className="form-field">
            <label>Unit:</label>
            <input
              type="text"
              name="measurement"
              value={item.measurement}
              onChange={(e) => handleInputChange(index, e)}
              disabled
            />
          </div>

          <div className="form-field">
            <label>Price:</label>
            <input
              type="number"
              name="price"
              value={item.price}
              onChange={(e) => handleInputChange(index, e)}
              
            />
          </div>

          <div className="form-field">
            <label>Total Price:</label>
            <input
              type="number"
              name="total price"
              value={item.totalItemPrice = item.quantity * item.price}
              onChange={(e) => handleInputChange(index, e)} 
              disabled
            />
          </div>

          {/* Remove Button */}
          <div className="remove-item">
            <button
              type="button"
              onClick={() => removeItemRow(index)}
              className="remove-button"
              disabled={items.length === 1} // Disable remove button if only one item
            >
              Remove Item
            </button>
          </div>
        </div>
      ))}

      {/* Button to add another item row */}
      <button type="button" className="add-another-item-supplier" onClick={addItemRow}>Add Another Item</button>

          {productsItems.map((product, index) => (
      <div key={index} className="form-row">
        <div className="form-field">
          <label>Products Item:</label>
          <select
            name="item"
            value={product.item}
            onChange={(e) => handleProductChange(index, e)}
          >
            <option value="">Select Item</option>
            {products.map((product, idx) => (
              <option key={idx} value={product.name}>
                {product.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label>Quantity:</label>
          <input
            type="number"
            name="quantity"
            value={product.quantity}
            onChange={(e) => handleProductChange(index, e)}
          />
        </div>

        <div className="form-field">
            <label>Unit:</label>
            <input
              type="text"
              name="measurement"
              value={product.measurement}
              onChange={(e) => handleInputChange(index, e)}
              disabled
            />
          </div>

        <div className="form-field">
          <label>Price:</label>
          <input
            type="number"
            name="price"
            value={product.price}
            onChange={(e) => handleProductChange(index, e)}
          />
        </div>

        <div className="form-field">
            <label>Total Price:</label>
            <input
              type="number"
              name="total price"
              value={product.totalProductPrice = product.quantity * product.price}
              onChange={(e) => handleInputChange(index, e)} 
              disabled
            />
          </div>

        {/* Remove Button */}
        <div className="remove-item">
          <button
            type="button"
            onClick={() => removeProductRow(index)}
            className="remove-button"
            disabled={productsItems.length === 1}
          >
            Remove Item
          </button>
        </div>
      </div>
    ))}

    <button type="button" className="add-another-item-supplier" onClick={addProductRow}>
      Add Another Item
    </button>

    <div className="form-row">
        <div className="form-field">
            <label>Shipping Fee:</label>
            <input
                type="number"
                name="shippingFee"
                value={items[0]?.shippingFee || ''}
                onChange={(e) => {
                    const updatedItems = [...items];
                    updatedItems[0] = {...updatedItems[0], shippingFee: e.target.value};
                    setItems(updatedItems);
                }}
                required
              />
          </div>
      </div>

        <button type="submit" className="save-entries-btn" disabled={isLoading}>
          {isLoading ? 'Submitting...' : 'Submit'}
        </button>

        <div className="overalltotal-container">
        <div className="totalshipping-container">
          <span className="total-label">Subtotal: ₱</span>
          <span className="total-value">{subtotal}</span>
        </div>
        <div className="totalshipping-container">
            <span className="total-label">Shipping Fee: ₱</span>
            <span className="total-value">{shippingFee}</span>
        </div>
        <div className="totalshipping-container">
            <span className="total-label">Total Amount: ₱</span>
            <span className="total-value">{totalAmount}</span>
        </div>
      </div>

      <div className="user-email">
        <span>Email: {lastUpdatedBy}</span>
      </div>
    </form>
  );
};

export default SuppliersAddReceipt;